<template>
  <v-list nav>
    <SideMenuListItem
      v-if="showLookerReport"
      route="ManagementDashboardTrendReport"
      icon="fas fa-chart-bar"
    >
      Management Dashboard Trend Report
    </SideMenuListItem>

    <template v-if="!isRestrictedUser">
      <SideMenuListItem
        v-if="showDemo"
        :disabled="!showDemo"
        :facility-id="currentFacilityId"
        route="PageManagementDashboard"
        icon="fas fa-tachometer-alt"
      >
        Management Summary
      </SideMenuListItem>
      <v-list-group
        v-if="
          showManagementSummary ||
          showOperationAndSecurity ||
          showMonitoringControlHubDashboard ||
          showVisualVerifyDashboard
        "
        color="default"
        group="management_summary|operation_and_security|monitor_and_control_hub|visual_verify"
      >
        <template #activator>
          <v-list-item-icon>
            <v-icon small>fas fa-tachometer-alt</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Dashboards</v-list-item-title>
        </template>
        <SideMenuListItem
          route="VisualVerifyEnterprise"
          icon="fas fa-video"
          v-if="showVisualVerifyDashboard"
        >
          VisualVerify
        </SideMenuListItem>
        <SideMenuListItem
          route="ManagementSummary"
          icon="fas fa-chart-pie"
          v-if="showManagementSummary"
        >
          Management Summary
        </SideMenuListItem>
        <SideMenuListItem
          v-if="showOperationAndSecurity"
          route="OperationAndSecurityDashboard"
          icon="fas fa-shield-alt"
        >
          Operations and Security
        </SideMenuListItem>

        <SideMenuListItem
          v-if="showMonitoringControlHubDashboard"
          route="MonitorAndControlHubDashboard"
          icon="fas fa-satellite-dish"
        >
          Monitoring & Control Hub Dashboard
        </SideMenuListItem>
      </v-list-group>

      <!-- Separate Organizational/Regional Dashboard item -->
      <SideMenuListItem
        v-if="
          !isFacilityManager &&
          (defaultOrganization?.type === 'organization' ||
            defaultOrganization?.type === 'region')
        "
        route="ManagementDashboard"
        icon="fas fa-map-marked-alt"
      >
        {{
          defaultOrganization?.type === "organization"
            ? "Organization Dashboard"
            : "Regional Dashboard"
        }}
      </SideMenuListItem>

      <!-- Selected Facility MenuList Items Start -->
      <SideMenuListItem
        :disabled="!currentFacilityId"
        :facility-id="currentFacilityId"
        route="FacilityDashboard"
        icon="fas fa-warehouse"
      >
        {{ facilityDashboardName }}
      </SideMenuListItem>
      <SideMenuListItem
        :disabled="!currentFacilityId"
        :facility-id="currentFacilityId"
        route="SmartUnits"
        icon="fas fa-table"
      >
        Smart Units
      </SideMenuListItem>

      <SideMenuListItem
        :disabled="!currentFacilityId"
        :facility-id="currentFacilityId"
        route="Renters"
        icon="fas fa-user-friends"
      >
        Renters
      </SideMenuListItem>

      <SideMenuListItem
        :disabled="inventoryUsesFacilityRoute ? !currentFacilityId : false"
        :facility-id="inventoryUsesFacilityRoute ? currentFacilityId : null"
        :route="
          inventoryUsesFacilityRoute ? 'Inventory' : 'OrganizationInventory'
        "
        icon="fas fa-dolly-flatbed"
      >
        Inventory
      </SideMenuListItem>

      <FeatureWrapper feature="smart_zones">
        <SideMenuListItem
          :disabled="!currentFacilityId"
          :facility-id="currentFacilityId"
          route="SmartZones"
          icon="fab fa-chromecast"
        >
          Smart Zones
        </SideMenuListItem>
      </FeatureWrapper>

      <FeatureWrapper feature="xpm_device_inventory">
        <SideMenuListItem
          :disabled="!currentFacilityId"
          :facility-id="currentFacilityId"
          route="RVDefenderInventory"
          icon="fas fa-rv"
        >
          RVDefender Inventory
        </SideMenuListItem>
      </FeatureWrapper>

      <SideMenuListItem
        :disabled="!currentFacilityId"
        :facility-id="currentFacilityId"
        route="ManageFacility"
        icon="fas fa-warehouse"
      >
        Manage Facility
      </SideMenuListItem>

      <SideMenuListItem
        v-if="
          showTaskManagement &&
          (defaultOrganizationType !== 'facility' || reachesMultipleFacilities)
        "
        :facility-id="currentFacilityId"
        route="ManageTasks"
        icon="fa-solid fa-calendar-check"
      >
        Manage Tasks
      </SideMenuListItem>

      <SideMenuListItem
        :facility-id="currentFacilityId"
        :route="currentFacilityId ? 'Reports' : 'ReportsOrganization'"
        icon="fas fa-file-chart-line"
      >
        Reports
      </SideMenuListItem>

      <SideMenuListItem
        v-if="canAdministerUsers"
        :facility-id="currentFacilityId"
        :route="currentFacilityId ? 'Users' : 'UsersOrganization'"
        icon="fas fa-users"
      >
        Users
      </SideMenuListItem>

      <SideMenuListItem
        :facility-id="currentFacilityId"
        :route="currentFacilityId ? 'Notifications' : 'UserNotifications'"
        icon="fas fa-bells"
      >
        Notifications
      </SideMenuListItem>
      <SideMenuListItem
        :disabled="!currentFacilityId"
        :facility-id="currentFacilityId"
        route="AuditLog"
        icon="fas fa-history"
      >
        Audit Log
      </SideMenuListItem>
      <!-- Selected Facility MenuList Items End -->

      <v-divider class="my-2" />

      <SideMenuListItem route="Help" icon="mdi-help-circle">
        Help Resources
      </SideMenuListItem>

      <SideMenuListItem route="ContactUs" icon="fa-envelope">
        Contact Us
      </SideMenuListItem>

      <v-list-group
        color="default"
        group="slack_integration|teams_integration"
        v-if="
          (showSlackIntegration || showTeamsIntegration) &&
          defaultOrganizationType === 'organization'
        "
      >
        <template #activator>
          <v-list-item-icon>
            <v-icon small>fas fa-th</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Apps</v-list-item-title>
        </template>
        <SideMenuListItem
          route="Slack"
          icon="fab fa-slack"
          v-if="showSlackIntegration"
        >
          Slack integration
        </SideMenuListItem>

        <SideMenuListItem
          route="Teams"
          icon="fas fa-user"
          v-if="showTeamsIntegration"
        >
          Teams integration
        </SideMenuListItem>
      </v-list-group>
      <v-list-group v-if="showSettings" color="default" group="settings">
        <template #activator>
          <v-list-item-icon>
            <v-icon small>fas fa-cog</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Settings</v-list-item-title>
        </template>
        <SideMenuListItem
          route="SettingsEscalationMatrix"
          icon="mdi mdi-clipboard-check"
        >
          Incident & Task Management
        </SideMenuListItem>
      </v-list-group>
    </template>
  </v-list>
</template>

<script>
import useFacility from "../authentication/useFacility";
import SideMenuListItem from "./SideMenuListItem.vue";
import FeatureWrapper from "./FeatureWrapper.vue";
import useAccess from "@components/authentication/useAccess";
import useUser from "@components/authentication/useUser";
import useAccessibleScope from "@components/authentication/useAccessibleScope";

export default {
  components: {
    FeatureWrapper,
    SideMenuListItem,
  },
  setup() {
    const { currentFacilityId, currentFacility } = useFacility();
    const {
      fetchOrganizationFeatures,
      showManagementSummary,
      showOperationAndSecurity,
      showMonitoringControlHubDashboard,
      showTaskManagement,
      showSlackIntegration,
      showTeamsIntegration,
      showVisualVerifyDashboard,
      showSettings,
    } = useAccess();
    const {
      defaultOrganization,
      currentUser,
      isFacilityManager,
      canAdministerUsers,
      canViewOrganizationTree,
    } = useUser();
    const { reachesMultipleFacilities, singleFacilityScope } =
      useAccessibleScope();

    return {
      reachesMultipleFacilities,
      singleFacilityScope,
      currentUser,
      currentFacilityId,
      currentFacility,
      showManagementSummary,
      showOperationAndSecurity,
      showMonitoringControlHubDashboard,
      fetchOrganizationFeatures,
      showTaskManagement,
      showSlackIntegration,
      showTeamsIntegration,
      showSettings,
      defaultOrganization,
      isFacilityManager,
      canAdministerUsers,
      showVisualVerifyDashboard,
    };
  },
  computed: {
    facilityDashboardName() {
      return (this.defaultOrganization?.type !== "facility" &&
        this.defaultOrganization?.type !== "platform") ||
        this.reachesMultipleFacilities
        ? "Facility Dashboard"
        : "Dashboard";
    },
    defaultOrganizationType() {
      return this.defaultOrganization?.type;
    },
    isSelectedFacility() {
      return (
        this.currentFacility?.type === "facility" &&
        !this.reachesMultipleFacilities
      );
    },
    // The Inventory item points at the facility-scoped "Inventory" route (needs
    // organization_id) for a selected facility OR any single-scope user, and at
    // the param-less "OrganizationInventory" otherwise. All three bindings
    // (route / facility-id / disabled) must share this flag — otherwise the
    // route can resolve to "Inventory" with no organization_id and vue-router
    // logs a "missing param" warning.
    inventoryUsesFacilityRoute() {
      return (
        (this.currentFacilityId && this.currentFacility?.type === "facility") ||
        (!this.reachesMultipleFacilities && this.singleFacilityScope)
      );
    },
    isRestrictedUser() {
      return this.currentUser?.username === "cpotash@citylinecapital.com";
    },
    showLookerReport() {
      const authorizedUsers = [
        "cpotash@citylinecapital.com",
        "helen@storage-defender.com",
      ];
      return authorizedUsers.includes(this.currentUser?.username);
    },
    showDemo() {
      return [
        "dc17ec00-e95c-4de5-a4cf-3b5786ad165a",
        "7a10ad54-a6f8-4d25-b9b8-82b1efa89f1b",
      ].includes(this.currentFacilityId);
    },
  },
  beforeMount() {
    this.fetchOrganizationFeatures(this.$apollo);
  },
};
</script>
